// Custom Volumental Scan Order Integration
// Purpose: Allows customers to add foot scans to their orders after purchase
// Dependencies: Volumental SDK (loaded dynamically)
// Settings: custom_enable_volumental_scan_order
//
// TODO: MIGRATION NOTE - When unified notification system is merged:
// - Notification classes in HTML: measurement-checker__notification* → validation-notification*
// - Spinner classes remain the same (measurement-checker__spinner)
// - No changes needed to this JavaScript file since we only reference HTML element IDs

(function () {
  'use strict';

  // Guard against multiple loads
  if (window.CustomVolumentalScanOrder) {
    return;
  }

  window.CustomVolumentalScanOrder = class {
    constructor(element) {
      this.element = element;
      this.form = null;
      this.submitButton = null;
      this.errorMessage = null;
      this.widgetContainer = null;
      this.notificationMessage = null;
      this.successMessage = null;
      this.orderDetails = null;
      this.measurementSubmitted = false;
      this.abortController = new AbortController();

      // Bypass mode state — see docs/projects/cs-scan-bypass/PLAN.md
      this.bypassEnabled = this.element.dataset.bypassEnabled === 'true';
      this.bypassCode = (this.element.dataset.bypassCode || '').trim().toLowerCase();
      this.bypassMode = false;
      this.bypassFieldsShown = false;

      this.init();
    }

    init() {
      if (!this.element) return;
      
      const DEBUG = !!(window.Shopify && window.Shopify.designMode);
      if (DEBUG) console.log('CustomVolumentalScanOrder: Initialized for element', this.element);
      
      this.cacheElements();
      this.setupEventListeners();
      this.handleUrlParameters();
    }

    cacheElements() {
      this.form = this.element.querySelector('#add-scan-form');
      this.submitButton = this.element.querySelector('#submit_button');
      this.errorMessage = this.element.querySelector('#error-message');
      this.warningMessage = this.element.querySelector('#warning-message');
      this.widgetContainer = this.element.querySelector('#volumental-widget-container');
      this.notificationMessage = this.element.querySelector('#notification-message');
      this.successMessage = this.element.querySelector('#success-message');
      this.orderDetails = this.element.querySelector('#order-details');

      this.orderNumberInput = this.element.querySelector('#order_number');
      this.emailInput = this.element.querySelector('#email');

      // Bypass mode elements (only rendered when bypass is enabled in section settings)
      this.orderNumberField = this.element.querySelector('[data-order-number-field]');
      this.bypassFields = this.element.querySelector('[data-bypass-fields]');
      this.bypassFirstNameInput = this.element.querySelector('#bypass_first_name');
      this.bypassLastNameInput = this.element.querySelector('#bypass_last_name');
      
      const DEBUG = !!(window.Shopify && window.Shopify.designMode);
      if (DEBUG) {
        console.log('CustomVolumentalScanOrder: Cached elements:');
        console.log('  form:', this.form);
        console.log('  orderNumberInput:', this.orderNumberInput);
        console.log('  emailInput:', this.emailInput);
        console.log('  All inputs in element:', this.element.querySelectorAll('input'));
        this.element.querySelectorAll('input').forEach((input, index) => {
          console.log(`  Input ${index}:`, {
            id: input.id,
            name: input.name,
            type: input.type,
            element: input
          });
        });
      }
    }

    setupEventListeners() {
      if (!this.form) return;

      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));

      // Option D: while in bypass mode, edits to either keystone field take the
      // customer "back a step" — name fields hide and bypass state clears, but
      // the values they were typing into (order number, email) stay intact.
      // Only wire these up when bypass is enabled to keep listeners minimal.
      if (this.bypassEnabled) {
        if (this.orderNumberInput) {
          this.orderNumberInput.addEventListener('input', this.handleOrderNumberInput.bind(this));
        }
        if (this.emailInput) {
          this.emailInput.addEventListener('input', this.handleEmailInput.bind(this));
        }
      }
    }

    handleOrderNumberInput() {
      if (!this.bypassMode) return;
      // Order number still matches the sentinel — no-op, customer hasn't backed out.
      if (this._isBypassTrigger(this.orderNumberInput.value)) return;

      this._resetBypassState();
    }

    handleEmailInput() {
      if (!this.bypassMode) return;
      this._resetBypassState();
    }

    _resetBypassState() {
      const DEBUG = !!(window.Shopify && window.Shopify.designMode);
      if (DEBUG) console.log('CustomVolumentalScanOrder: Resetting bypass state');

      this.bypassMode = false;
      this.bypassFieldsShown = false;

      if (this.bypassFields) this.bypassFields.hidden = true;
      if (this.bypassFirstNameInput) this.bypassFirstNameInput.value = '';
      if (this.bypassLastNameInput) this.bypassLastNameInput.value = '';
      // Email is intentionally left intact — the customer was probably just
      // correcting something earlier in the flow, not starting from scratch.

      this.resolvedEmail = null;
      this.resolvedFirstName = null;
      this.resolvedLastName = null;

      this.hideAllMessages();

      // Restore default button text in case future changes diverge from "Continue".
      const buttonText = this.submitButton && this.submitButton.querySelector('.measurement-checker__button-text');
      if (buttonText) buttonText.textContent = 'Continue';
    }

    handleFormSubmit(event) {
      event.preventDefault();
      
      const DEBUG = !!(window.Shopify && window.Shopify.designMode);
      
      // Re-cache elements if they're null (defensive programming)
      if (!this.orderNumberInput || !this.emailInput) {
        if (DEBUG) console.log('CustomVolumentalScanOrder: Re-caching elements due to null inputs');
        this.cacheElements();
      }
      
      if (!this.orderNumberInput) {
        console.error('CustomVolumentalScanOrder: Order number input not found');
        this.showNotification('error', 'Form Error', 'Order number field not found. Please refresh the page.');
        return;
      }
      
      if (!this.emailInput) {
        console.error('CustomVolumentalScanOrder: Email input not found');
        this.showNotification('error', 'Form Error', 'Email field not found. Please refresh the page.');
        return;
      }
      
      const orderNumber = this.orderNumberInput.value;
      const email = this.emailInput.value;

      if (DEBUG) {
        console.log('CustomVolumentalScanOrder: Form submitted with:', { orderNumber, email });
      }

      // Bypass mode: sentinel match routes through the no-order flow.
      // Once the bypass fields are revealed, subsequent submits are bypass-bound.
      if (this._isBypassTrigger(orderNumber) || this.bypassMode) {
        this.processBypassEntry(email);
        return;
      }

      this.processForm(orderNumber, email, true);
    }

    _isBypassTrigger(value) {
      if (!this.bypassEnabled || !this.bypassCode) return false;
      return (value || '').trim().toLowerCase() === this.bypassCode;
    }

    processBypassEntry(email) {
      const DEBUG = !!(window.Shopify && window.Shopify.designMode);

      // Phase 1: reveal name fields, hide the order number field, relabel the button.
      if (!this.bypassFieldsShown) {
        if (!this.bypassFields) {
          if (DEBUG) console.error('CustomVolumentalScanOrder: bypass fields container missing');
          return;
        }

        this.bypassMode = true;
        this.bypassFieldsShown = true;
        this.hideAllMessages();

        // Option D: keep the order number field visible so the customer can
        // correct a fat-fingered sentinel. Edits trigger _resetBypassState().
        this.bypassFields.hidden = false;

        const buttonText = this.submitButton && this.submitButton.querySelector('.measurement-checker__button-text');
        if (buttonText) buttonText.textContent = 'Continue';

        if (this.bypassFirstNameInput) {
          this.bypassFirstNameInput.focus();
        }
        return;
      }

      // Phase 2: validate names and start the scan.
      const firstName = (this.bypassFirstNameInput && this.bypassFirstNameInput.value || '').trim();
      const lastName = (this.bypassLastNameInput && this.bypassLastNameInput.value || '').trim();
      const trimmedEmail = (email || '').trim();

      const missing = [];
      if (!trimmedEmail) missing.push('email');
      if (!firstName) missing.push('first name');
      if (!lastName) missing.push('last name');

      if (missing.length > 0) {
        this.showNotification('error', 'Missing Information',
          `Please enter your ${missing.join(', ')} to continue.`
        );
        return;
      }

      this.resolvedEmail = trimmedEmail.toLowerCase();
      this.resolvedFirstName = firstName;
      this.resolvedLastName = lastName;

      this.hideAllMessages();
      this.showNotification('success', 'Let\'s Begin Your Scan', [
        'Please go ahead and scan by using the <b>Scan your feet.</b> button below.',
        '<b>Important:</b> After scanning your feet, be sure to confirm your scan by closing the scanning widget and then selecting <b>Confirm</b> on this page to complete the process.'
      ]);

      // Mirror the normal flow's URL update so the Volumental QR carries enough
      // state for the desktop-to-mobile handoff to resume bypass on the phone.
      this._writeBypassUrlState();

      // No orderNumber to pass through; downstream branches on this.bypassMode.
      this.initVolumentalWidget(null, this.resolvedEmail);
    }

    _writeBypassUrlState() {
      const params = new URLSearchParams();
      params.set('orderNumber', this.orderNumberInput.value);
      params.set('email', this.resolvedEmail);
      params.set('firstName', this.resolvedFirstName);
      params.set('lastName', this.resolvedLastName);
      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }

    async _resumeBypassFromUrl({ orderNumber, email, firstName, lastName }) {
      // Mobile arriving via the Volumental QR. Pre-fill everything, mark the
      // bypass UI as already past phase 1, then run phase 2 to start the scan.
      this.orderNumberInput.value = orderNumber;
      this.emailInput.value = email;
      if (this.bypassFirstNameInput) this.bypassFirstNameInput.value = firstName;
      if (this.bypassLastNameInput) this.bypassLastNameInput.value = lastName;

      this.bypassMode = true;
      this.bypassFieldsShown = true;
      if (this.bypassFields) this.bypassFields.hidden = false;

      // The normal flow's order-lookup awaits inadvertently let the async-loaded
      // Volumental SDK finish defining the custom element. The bypass URL handoff
      // skips those awaits, so wait explicitly — otherwise we set display:block
      // on an element the SDK hasn't upgraded yet and the widget never renders.
      await this._waitForVolumentalSDK();

      this.processBypassEntry(email);
    }

    _waitForVolumentalSDK(timeoutMs = 5000) {
      if (!window.customElements) return Promise.resolve();

      const defined = window.customElements.whenDefined('volumental-measurement');
      const timeout = new Promise((resolve) => setTimeout(resolve, timeoutMs));
      return Promise.race([defined, timeout]);
    }

    async processForm(orderNumber, email, updateURL = true) {
      this.disableButtonAndShowSpinner(this.submitButton);
      this.hideAllMessages();

      try {
        // Look up the order first
        const response = await this.fetchWithTimeout(
          'https://store-signin.wiivvapi.com/api/scans/getOrderByEmailAndOrderNumber',
          {
            method: 'POST',
            body: JSON.stringify({
              orderNumber: orderNumber,
              email: email
            })
          }
        );

        const data = await response.json();

        if (!response.ok) {
          this.showNotification('warning', 'We Couldn\'t Find Your Order',
            'We couldn\'t find a matching order. Please check your order number and email address, then try again.'
          );
          return;
        }

        // Resolve the foot scan email from the order's line item properties,
        // falling back to the user-entered email if not present.
        // Store on the instance so it's available at scan submission time.
        this.resolvedEmail = this._getFootScanEmail(data, email);

        // Check if the resolved email already has measurements on file
        const validateResponse = await this.fetchWithTimeout(
          'https://store-signin.wiivvapi.com/api/wiivvAuth/validate',
          {
            method: 'POST',
            body: JSON.stringify({ email: this.resolvedEmail })
          }
        );

        const validateData = await validateResponse.json();

        if (validateData.userExists && validateData.isMeasurementReady) {
          this.showNotification('success', 'Measurements Already On File',
            'We have measurements for you already so you\'re all set! If you have any questions or you think there\'s an issue with your order, contact us at <a href="mailto:care@fitmyfoot.com?subject=Question%20about%20my%20order%20(via%20%22Add%20Scan%20to%20Order%22%20page)">care@fitmyfoot.com</a>!'
          );
          return;
        }

        this.displayOrderDetails(data);
        this.initVolumentalWidget(orderNumber, email);

        if (updateURL) {
          const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?orderNumber=${encodeURIComponent(orderNumber)}&email=${encodeURIComponent(email)}`;
          window.history.replaceState({}, '', newUrl);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          this.showNotification('error', 'Request Timeout', 'Request timed out. Please try again.');
        } else {
          this.showNotification('error', 'Error', 'An error occurred. Please try again.');
        }
      } finally {
        this.enableButtonAndHideSpinner(this.submitButton);
      }
    }

    _getFootScanEmail(orderData, fallbackEmail) {
      for (const item of orderData.lineItems || []) {
        for (const prop of item.extraProperties || []) {
          if (prop.name === 'Email Used for Foot Scan Check' && prop.value && prop.value.trim()) {
            return prop.value.trim().toLowerCase();
          }
        }
      }
      return fallbackEmail;
    }

    displayOrderDetails(data) {
      // Show structured success message for order found
      this.showNotification('success', 'We Found Your Order', [
        'Great! We\'ve found your order details which we\'ve included below for your review.',
        'Please go ahead and scan by using the <b>Scan your feet.</b> button below.',
        '<b>Important:</b> After scanning your feet, be sure to confirm your scan by closing the scanning widget and then selecting <b>Confirm</b> on this page to complete the process.'
      ]);

      const orderDate = new Date(data.date);
      const formattedDate = orderDate.toLocaleDateString();

      let detailsHTML = `
        <p><strong>Order #:</strong> ${data.orderNumber.replace('#', '')}</p>
        <p><strong>Total:</strong> ${data.currency} $${data.total}</p>
        <p><strong>Date:</strong> ${formattedDate}</p>
        <p><strong>Items:</strong></p>
        <ul style="list-style-type: none; padding-left: 0;">
      `;

      data.lineItems.forEach((item, index) => {
        detailsHTML += `
          <li style="margin-left: 20px;">${index + 1}. ${item.productName}
            <div style="margin-left: 20px;">• Variant: ${item.productVariantDescription}</div>
            <div style="margin-left: 20px;">• Quantity: ${item.quantity}</div>
          </li>
        `;
      });

      detailsHTML += '</ul>';

      this.orderDetails.innerHTML = detailsHTML;
      this.orderDetails.style.display = 'block';
      
      const volumentalElement = this.element.querySelector('#volumental-measurement');
      if (volumentalElement) {
        volumentalElement.style.display = 'block';
      }
    }

    initVolumentalWidget(orderNumber, email) {
      this.widgetContainer.style.display = 'block';

      const volumentalElement = this.element.querySelector('#volumental-measurement');

      if (volumentalElement) {
        // Ensure the inner widget element is visible. hideAllMessages() hides it,
        // and only displayOrderDetails() re-shows it in the non-bypass path.
        volumentalElement.style.display = 'block';

        const DEBUG = !!(window.Shopify && window.Shopify.designMode);
        if (DEBUG) console.log('CustomVolumentalScanOrder: Attaching Volumental event listeners');

        // Listener for on-opened event
        volumentalElement.addEventListener('volumental:on-opened', (e) => {
          if (DEBUG) console.log('CustomVolumentalScanOrder: Volumental widget opened', e);
        });

        // Listener for on-measurement event
        volumentalElement.addEventListener('volumental:on-measurement', (e) => {
          if (this.measurementSubmitted) return;
          
          if (DEBUG) console.log('CustomVolumentalScanOrder: Measurement received', e);
          const scanId = e.detail.id;
          
          if (scanId) {
            // Get the internal scan ID from cookies for API validation
            const internalVolumentalScanId = this.getCookie('__volumental_sdk_scanId');
            if (DEBUG) console.log('Internal Volumental scan ID:', internalVolumentalScanId);
            
            // GraphQL query to get detailed measurement data
            const graphqlPayload = {
              operationName: "getMeasurementResultQuery",
              variables: {
                scanID: internalVolumentalScanId,
                population: "UNISEX"
              },
              query: `
                query getMeasurementResultQuery($scanID: ID!, $population: Population!) {
                  configuration {
                    unitSystem
                    __typename
                  }
                  ...ScanQueryFragment
                }
                
                fragment ScanQueryFragment on Query {
                  scan(input: {scanID: $scanID}) {
                    id
                    publicID
                    createdAt
                    measurement {
                      leftLength
                      leftWidth
                      leftInstepHeight
                      rightLength
                      rightWidth
                      rightInstepHeight
                      compareToPopulation(population: $population) {
                        leftLength
                        leftWidth
                        leftInstepHeight
                        rightLength
                        rightWidth
                        rightInstepHeight
                        __typename
                      }
                      __typename
                    }
                    meshes {
                      leftFoot
                      rightFoot
                      __typename
                    }
                    ... on RetailScan {
                      gender
                      ageGroup
                      __typename
                    }
                    ... on WebScan {
                      claimed
                      __typename
                    }
                    __typename
                  }
                  __typename
                }
              `
            };

            // Make API call to Volumental for detailed measurement data
            fetch('https://widget.volumental.com/query', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': '*/*',
                'x-visa-token': '8906be05-6a77-479f-827c-f8d4f30e0336',
                'rid': 'anti-csrf'
              },
              body: JSON.stringify(graphqlPayload)
            })
            .then(response => {
              if (!response.ok) {
                throw new Error(`Network response was not ok (Status: ${response.status})`);
              }
              return response.json();
            })
            .then(data => {
              const measurement = data?.data?.scan?.measurement;

              if (measurement) {
                const { leftLength, rightLength } = measurement;
                if (DEBUG) console.log('Measurement data - Left Length:', leftLength, 'Right Length:', rightLength);
                if (DEBUG) console.log('Full measurement data:', measurement);
                
                // Validate measurement sizes (configurable range)
                const minSize = parseFloat(this.element.dataset.sizeValidationMin) || 0.21125;
                const maxSize = parseFloat(this.element.dataset.sizeValidationMax) || 0.3102;
                
                if (leftLength < minSize || leftLength > maxSize || rightLength < minSize || rightLength > maxSize) {
                  // Measurements are out of range
                  const contactEmail = this.element.dataset.outOfRangeContactEmail || 'care@fitmyfoot.com';
                  const outOfRangeTitle = this.element.dataset.outOfRangeTitle || 'We Couldn\'t Find Your Fit';
                  const outOfRangeMessage = this.element.dataset.outOfRangeMessage || 'Your foot scan results are outside our current size range. We\'re working to expand our offerings soon!';
                  
                  this.showNotification('warning', outOfRangeTitle, [
                    outOfRangeMessage,
                    `For updates or questions, contact us at <a href="mailto:${contactEmail}?subject=Out of size range">${contactEmail}</a>`
                  ], { autoScroll: true });
                  
                  // Hide the widget container since this scan can't be used
                  this.widgetContainer.style.display = 'none';
                  return;
                } else {
                  // Measurements are within range - proceed with success flow
                  this.processMeasurementSuccess(scanId, orderNumber);
                }
              } else {
                if (DEBUG) console.error('Measurement data is unavailable in the response.');
                this.showNotification('error', 'Error Processing Scan', 'There was an error processing your scan. Please try again.');
              }
            })
            .catch(error => {
              if (DEBUG) console.error('Error fetching data from Volumental API:', error);
              this.showNotification('error', 'Error Processing Scan', 'There was an error processing your scan. Please try again.');
            });
          } else {
            if (DEBUG) console.error('Scan ID is not available in the event details.');
            this.showNotification('error', 'Error Processing Scan', 'There was an error processing your scan. Please try again.');
          }
        });
        
        // Listener for on-closed event
        volumentalElement.addEventListener('volumental:on-closed', (e) => {
          const DEBUG = !!(window.Shopify && window.Shopify.designMode);
          if (DEBUG) console.log('CustomVolumentalScanOrder: Volumental widget closed', e);
        });
      } else {
        console.error('CustomVolumentalScanOrder: volumental-measurement element not found');
        this.showNotification('error', 'Initialization Error', 'Failed to initialize foot scanning widget. Please refresh the page and try again.');
      }
    }

    async fetchWithTimeout(url, options, timeout = null) {
      // Get timeout from data attribute (same pattern as measurement checker)
      const actualTimeout = timeout || (parseInt(this.element.dataset.apiTimeout) * 1000 || 10000);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), actualTimeout);
      
      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            ...options.headers
          }
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    }

    /**
     * Standardized notification display method
     * @param {string} type - 'error', 'warning', 'info', or 'success'
     * @param {string} title - Notification title
     * @param {string|Array} content - Single message or array of messages/HTML strings
     * @param {Object} options - Additional options (autoScroll, appendContent, keepOthers)
     */
    showNotification(type, title, content, options = {}) {
      // Map type to element
      const elementMap = {
        'error': this.errorMessage,
        'warning': this.warningMessage,
        'info': this.notificationMessage,
        'success': this.successMessage
      };
      
      const element = elementMap[type];
      if (!element) {
        console.error('Invalid notification type:', type);
        return;
      }
      
      // Hide other notifications unless keeping them or appending
      if (!options.appendContent && !options.keepOthers) {
        // Hide all notification elements except the one we're using
        if (this.errorMessage && this.errorMessage !== element) this.errorMessage.style.display = 'none';
        if (this.warningMessage && this.warningMessage !== element) this.warningMessage.style.display = 'none';
        if (this.notificationMessage && this.notificationMessage !== element) this.notificationMessage.style.display = 'none';
        if (this.successMessage && this.successMessage !== element) this.successMessage.style.display = 'none';
      }
      
      // Clear the element we're about to use unless appending
      if (!options.appendContent) {
        element.innerHTML = '';
      }
      
      // Create structured notification
      const titleEl = document.createElement('div');
      titleEl.className = 'measurement-checker__notification-title';
      titleEl.textContent = title;
      element.appendChild(titleEl);
      
      const divider = document.createElement('hr');
      divider.className = 'measurement-checker__notification-divider';
      element.appendChild(divider);
      
      const contentEl = document.createElement('div');
      contentEl.className = 'measurement-checker__notification-content';
      
      // Handle content as array or single string
      const messages = Array.isArray(content) ? content : [content];
      messages.forEach(message => {
        const paragraph = document.createElement('p');
        // Check if message contains HTML
        if (message.includes('<')) {
          paragraph.innerHTML = message;
        } else {
          paragraph.textContent = message;
        }
        contentEl.appendChild(paragraph);
      });
      
      element.appendChild(contentEl);
      element.style.display = 'block';
      
      // Auto-scroll if requested
      if (options.autoScroll) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, options.scrollDelay || 300);
      }
      
      return element;
    }

    getButtonIconSVG(iconType, position) {
      const iconClass = `measurement-checker__button-icon measurement-checker__button-icon--${position}`;
      let pathContent = '';
      
      switch(iconType) {
        case 'arrow_right':
          pathContent = '<path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path>';
          break;
        case 'search':
          pathContent = '<circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path>';
          break;
        case 'check':
          pathContent = '<path d="M20 6 9 17l-5-5"></path>';
          break;
        case 'mail':
          pathContent = '<rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-10 5L2 7"></path>';
          break;
        case 'user':
          pathContent = '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle>';
          break;
      }
      
      return `<svg class="${iconClass}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${pathContent}</svg>`;
    }


    hideAllMessages() {
      this.errorMessage.style.display = 'none';
      this.warningMessage.style.display = 'none';
      this.notificationMessage.style.display = 'none';
      this.successMessage.style.display = 'none';
      this.orderDetails.style.display = 'none';
      
      const volumentalElement = this.element.querySelector('#volumental-measurement');
      if (volumentalElement) {
        volumentalElement.style.display = 'none';
      }
    }

    disableButtonAndShowSpinner(button) {
      if (!button) return;
      
      button.disabled = true;
      button.style.color = 'transparent';

      let wrapper = button.parentElement;
      if (!wrapper.classList.contains('spinner-wrapper')) {
        wrapper = document.createElement('div');
        wrapper.classList.add('spinner-wrapper');
        wrapper.style.position = 'relative';
        wrapper.style.display = 'flex';
        wrapper.style.width = '100%';
        wrapper.style.justifyContent = 'center';

        button.parentElement.insertBefore(wrapper, button);
        wrapper.appendChild(button);
      }

      if (!wrapper.querySelector('.measurement-checker__spinner')) {
        const spinner = document.createElement('span');
        spinner.className = 'measurement-checker__spinner';
        wrapper.appendChild(spinner);
      }
    }

    enableButtonAndHideSpinner(button) {
      if (!button) return;
      
      button.disabled = false;
      button.style.color = '';
      
      const wrapper = button.parentElement;
      if (wrapper.classList.contains('spinner-wrapper')) {
        const spinner = wrapper.querySelector('.measurement-checker__spinner');
        if (spinner) {
          spinner.remove();
        }
        const buttonContainer = wrapper.parentElement;
        buttonContainer.insertBefore(button, wrapper);
        wrapper.remove();
      }
    }

    handleUrlParameters() {
      const params = new URLSearchParams(window.location.search);
      const orderNumber = params.get('orderNumber');
      const email = params.get('email');

      if (!orderNumber || !email) return;

      // Bypass handoff: the desktop's URL update encodes the sentinel plus
      // first/last name so the Volumental QR-scanned mobile page can resume
      // the bypass flow without going through phase 1 again.
      if (this._isBypassTrigger(orderNumber)) {
        const firstName = params.get('firstName');
        const lastName = params.get('lastName');

        if (firstName && lastName) {
          this._resumeBypassFromUrl({ orderNumber, email, firstName, lastName });
        } else {
          // Sentinel in URL but no names — pre-fill the visible fields and let
          // the customer click Continue to enter phase 1 manually.
          this.orderNumberInput.value = orderNumber;
          this.emailInput.value = email;
        }
        return;
      }

      this.orderNumberInput.value = orderNumber;
      this.emailInput.value = email;
      this.processForm(orderNumber, email, false);
    }

    getCookie(name) {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for(let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    }

    processMeasurementSuccess(scanId, orderNumber) {
      // Display structured confirmation message with auto-scroll
      // Don't hide other notifications - user should still see order details
      const confirmPrompt = this.bypassMode
        ? 'Please confirm you would like this scan added to your FitMyFoot profile.'
        : 'Please confirm you would like this scan added to your FitMyFoot order: #' + orderNumber;

      this.showNotification('info', 'Scan Complete: Please Confirm To Finish', [
        confirmPrompt,
        '(If you\'d like to re-scan, you can do so by selecting <b>See your measurements</b> above and then tapping on <b>Start over</b>.)'
      ], { autoScroll: true, keepOthers: true });

      // Create "Add Scan to Order" button with icon support
      const buttonWrapper = document.createElement('div');
      buttonWrapper.className = 'volumental-scan__button-wrapper';
      buttonWrapper.style.marginTop = '1rem';
      
      const addButton = document.createElement('button');
      addButton.id = 'add-scan-button';
      addButton.type = 'button';
      
      // Get button settings from section data attributes
      const showIcon = this.element.dataset.showButtonIcon === 'true';
      const iconType = this.element.dataset.buttonIconType || 'check';
      const iconPosition = this.element.dataset.buttonIconPosition || 'right';
      
      // Set button classes conditionally based on icon setting (using primary style for confirmation)
      addButton.className = `measurement-checker__button ${showIcon ? 'measurement-checker__button--with-icon' : ''} button`;
      if (showIcon) {
        addButton.style.setProperty('--icon-size', 'var(--button-icon-size)');
      }
      
      // Build button HTML with optional icon
      let buttonHTML = '';
      
      if (showIcon && iconPosition === 'left') {
        buttonHTML += this.getButtonIconSVG(iconType, 'left');
      }
      
      buttonHTML += '<span class="measurement-checker__button-text">Confirm & Add Scan to Order</span>';
      
      if (showIcon && iconPosition === 'right') {
        buttonHTML += this.getButtonIconSVG(iconType, 'right');
      }
      
      addButton.innerHTML = buttonHTML;

      buttonWrapper.appendChild(addButton);
      
      // Append button to the existing notification
      if (this.notificationMessage.style.display === 'block') {
        this.notificationMessage.appendChild(buttonWrapper);
      }

      // Event listener for "Add Scan to Order" button
      addButton.addEventListener('click', async (event) => {
        const email = this.resolvedEmail || this.emailInput.value;

        this.disableButtonAndShowSpinner(addButton);
        const buttonText = addButton.querySelector('.measurement-checker__button-text');
        if (buttonText) {
          buttonText.textContent = 'Adding Scan...';
        }

        try {
          const response = this.bypassMode
            ? await this.fetchWithTimeout(
                'https://store-signin.wiivvapi.com/api/scans/addVolumentalScanWithoutOrder',
                {
                  method: 'POST',
                  body: JSON.stringify({
                    email: email,
                    firstName: this.resolvedFirstName,
                    lastName: this.resolvedLastName,
                    volumentalScanId: scanId,
                    volumentalScanDevice: 'widget'
                  })
                }
              )
            : await this.fetchWithTimeout(
                'https://store-signin.wiivvapi.com/api/scans/addVolumentalScanWithOrderValidation',
                {
                  method: 'POST',
                  body: JSON.stringify({
                    email: email,
                    orderNumber: orderNumber,
                    volumentalScanId: scanId,
                    volumentalScanDevice: 'widget'
                  })
                }
              );

          if (response.ok) {
            const successTitle = this.bypassMode
              ? 'Success! Scan Added to Your Profile'
              : 'Success! Scan Added to Order';
            const successBody = this.bypassMode
              ? [
                  'Your foot scan has been successfully added to your FitMyFoot profile.',
                  'You\'re all set! We\'ll use your measurements to create your custom footwear.'
                ]
              : [
                  `Your foot scan has been successfully added to your FitMyFoot order #${orderNumber}.`,
                  'You\'re all set! We\'ll use your measurements to create your custom footwear.'
                ];

            this.showNotification('success', successTitle, successBody, { autoScroll: true });

            this.widgetContainer.style.display = 'none';
            this.measurementSubmitted = true;
          } else {
            throw new Error('Failed to add scan');
          }
        } catch (error) {
          this.showNotification('error', 'Error', 'Failed to add scan. Please try again.');
          this.enableButtonAndHideSpinner(addButton);
          const buttonText = addButton.querySelector('.measurement-checker__button-text');
          if (buttonText) {
            buttonText.textContent = 'Confirm & Add Scan to Order';
          }
        }
      });
    }

    destroy() {
      // Cleanup event listeners
      if (this.form) {
        this.form.removeEventListener('submit', this.handleFormSubmit.bind(this));
      }

      // Abort any pending requests
      this.abortController.abort();

      // Clear references
      this.element = null;
      this.form = null;
      this.submitButton = null;
      this.errorMessage = null;
      this.warningMessage = null;
      this.widgetContainer = null;
      this.notificationMessage = null;
      this.successMessage = null;
      this.orderDetails = null;
      this.orderNumberField = null;
      this.bypassFields = null;
      this.bypassFirstNameInput = null;
      this.bypassLastNameInput = null;
    }
  };

  // Per-element initialization with tracking
  const SELECTOR = '[data-volumental-scan-order="true"]';
  const INIT_ATTR = 'data-volumental-scan-order-init';

  function initAll(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach((el) => {
      if (!el.hasAttribute(INIT_ATTR)) {
        const instance = new window.CustomVolumentalScanOrder(el);
        el.CustomVolumentalScanOrder = instance; // Store instance on element
        el.setAttribute(INIT_ATTR, 'true');
      }
    });
  }

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', () => initAll());

  // Theme editor support with proper cleanup
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => initAll(e.target));
    document.addEventListener('shopify:section:unload', (e) => {
      // Clean up initialized elements to prevent memory leaks
      e.target.querySelectorAll(`${SELECTOR}[${INIT_ATTR}]`).forEach(el => {
        el.removeAttribute(INIT_ATTR);
        // Call destroy() on the stored instance
        if (el.CustomVolumentalScanOrder && typeof el.CustomVolumentalScanOrder.destroy === 'function') {
          el.CustomVolumentalScanOrder.destroy();
        }
        el.CustomVolumentalScanOrder = null;
      });
    });
  }
})();

// Apply 14px border radius to Volumental widget shadow DOM
function applyVolumentalScanOrderBorderRadius() {
  const volumentalElement = document.getElementById('volumental-measurement');
  if (volumentalElement && volumentalElement.shadowRoot) {
    const container = volumentalElement.shadowRoot.querySelector('.container');
    if (container) {
      container.style.borderRadius = '14px';
      container.style.overflow = 'hidden';
      return true;
    }
  }
  return false;
}

// Initialize border radius handling for the widget
function initializeScanOrderVolumentalBorderRadius() {
  const volumentalElement = document.getElementById('volumental-measurement');
  if (!volumentalElement) return;
  
  // Poll for shadow DOM creation
  let attempts = 0;
  const poll = setInterval(() => {
    attempts++;
    const success = applyVolumentalScanOrderBorderRadius();
    
    if (success || attempts >= 20) {
      clearInterval(poll);
    }
  }, 50);
  
  // Watch for shadow DOM changes
  const observer = new MutationObserver(() => {
    if (volumentalElement.shadowRoot) {
      applyVolumentalScanOrderBorderRadius();
    }
  });
  observer.observe(volumentalElement, { childList: true, subtree: true });

  // Apply on widget events
  volumentalElement.addEventListener('volumental:on-opened', () => {
    applyVolumentalScanOrderBorderRadius();
  });
}

// Wait for Volumental SDK to be ready
function waitForScanOrderVolumentalSDK() {
  if (typeof window.Volumental !== 'undefined' || document.querySelector('volumental-measurement[defined]')) {
    initializeScanOrderVolumentalBorderRadius();
  } else {
    setTimeout(waitForScanOrderVolumentalSDK, 100);
  }
}

// Initialize Volumental widget when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize border radius handling
  waitForScanOrderVolumentalSDK();
  
  // Listen for SDK script load event
  const sdkScript = document.getElementById('volumental-sdk-script');
  if (sdkScript) {
    // If script already loaded
    if (sdkScript.complete) {
      setTimeout(initializeScanOrderVolumentalBorderRadius, 100);
    } else {
      // Add load event listener
      sdkScript.addEventListener('load', () => {
        setTimeout(initializeScanOrderVolumentalBorderRadius, 100);
      });
    }
  }
});

// Safari scroll fix - workaround for page scrolling to bottom when widget appears
window.addEventListener('load', () => {
  // Fallback to ensure border radius is applied on slow connections
  setTimeout(initializeScanOrderVolumentalBorderRadius, 500);
});